import { t } from '@lingui/macro'
import CloseIcon from '@mui/icons-material/Close'
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    TextField,
    Typography
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

export interface AdditionalPhoneData {
    phone: string | null
    label: string | null
    slot: 'phone1' | 'phone2'
}

interface AdditionalPhoneModalProps {
    open: boolean
    onClose: () => void
    onSave: (data: AdditionalPhoneData) => Promise<void>
    initialData?: {
        phone1: string | null
        phone1Label: string | null
        phone2: string | null
        phone2Label: string | null
    }
    loading?: boolean
    entityType?: 'patient' | 'candidate' | 'care-partner'
    editingSlot?: 'phone1' | 'phone2' // Optional: specify which slot is being edited
}
//form data
interface FormData {
    label: string
    phone: string
}

interface ValidationErrors {
    phone?: string
    label?: string
}

/**
 * Modal for adding a single additional phone number with label.
 * Automatically determines which slot (phone1 or phone2) to use.
 * Layout: Label on top, phone number below.
 */
export const AdditionalPhoneModal: React.FC<AdditionalPhoneModalProps> = ({
    open,
    onClose,
    onSave,
    initialData,
    loading = false,
    entityType = 'patient',
    editingSlot
}) => {
    // Determine which slot to use
    // If editingSlot is provided, use it; otherwise, determine automatically (phone1 if available, otherwise phone2)
    const slot: 'phone1' | 'phone2' = editingSlot || (initialData?.phone1 ? 'phone2' : 'phone1')

    // Memoize existingData to prevent unnecessary re-renders
    const existingData = useMemo(() => {
        return slot === 'phone1'
            ? { phone: initialData?.phone1 || '', label: initialData?.phone1Label || '' }
            : { phone: initialData?.phone2 || '', label: initialData?.phone2Label || '' }
    }, [slot, initialData?.phone1, initialData?.phone1Label, initialData?.phone2, initialData?.phone2Label])

    const [formData, setFormData] = useState<FormData>({
        label: existingData.label,
        phone: existingData.phone
    })

    const [errors, setErrors] = useState<ValidationErrors>({})
    const [hasChanges, setHasChanges] = useState(false)

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open) {
            setFormData({
                label: existingData.label,
                phone: existingData.phone
            })
            setErrors({})
            setHasChanges(false)
        }
    }, [open, existingData.label, existingData.phone])

    // Check for changes whenever form data changes
    useEffect(() => {
        if (open) {
            const hasFormChanges = formData.phone !== existingData.phone || formData.label !== existingData.label
            setHasChanges(hasFormChanges)
        }
    }, [formData, existingData, open])

    const validateForm = (): boolean => {
        const newErrors: ValidationErrors = {}

        const hasPhone = formData.phone.trim().length > 0
        const hasLabel = formData.label.trim().length > 0

        // If both are empty, that's fine (deleting the entry)
        if (!hasPhone && !hasLabel) {
            setErrors(newErrors)
            return true
        }

        // If phone exists, label is required
        if (hasPhone && !hasLabel) {
            newErrors.label = t`Label is required when phone number is provided`
        }

        // If label exists, phone is required
        if (hasLabel && !hasPhone) {
            newErrors.phone = t`Phone number is required when label is provided`
        }

        // Validate phone format if phone exists
        if (hasPhone) {
            // Check pattern: only digits, spaces, dashes, plus signs, parentheses
            if (!/^[0-9\s\-+()]+$/.test(formData.phone)) {
                newErrors.phone = t`Phone number can only contain digits, spaces, dashes, plus signs, and parentheses`
            }
            // Check that at least one digit is present
            else if (!/[0-9]/.test(formData.phone)) {
                newErrors.phone = t`Phone number must contain at least one digit`
            }
            // Check max length
            else if (formData.phone.length > 20) {
                newErrors.phone = t`Phone number must be 20 characters or less`
            }
        }

        // Validate label format if label exists
        if (hasLabel && formData.label.trim().length > 50) {
            newErrors.label = t`Label must be 50 characters or less`
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleInputChange = (field: keyof FormData, value: string) => {
        const newFormData = { ...formData, [field]: value }
        setFormData(newFormData)

        // Validate immediately as user types
        const hasPhone = field === 'phone' ? value.trim().length > 0 : formData.phone.trim().length > 0
        const hasLabel = field === 'label' ? value.trim().length > 0 : formData.label.trim().length > 0
        const newErrors: ValidationErrors = {}

        // If both are empty, that's fine (deleting the entry)
        if (!hasPhone && !hasLabel) {
            setErrors(newErrors)
            return
        }

        // If phone exists, label is required
        if (hasPhone && !hasLabel) {
            newErrors.label = t`Label is required when phone number is provided`
        }

        // If label exists, phone is required
        if (hasLabel && !hasPhone) {
            newErrors.phone = t`Phone number is required when label is provided`
        }

        // Validate phone format if phone exists
        if (hasPhone) {
            const phoneValue = field === 'phone' ? value : formData.phone
            if (!/^[0-9\s\-+()]+$/.test(phoneValue)) {
                newErrors.phone = t`Phone number can only contain digits, spaces, dashes, plus signs, and parentheses`
            } else if (!/[0-9]/.test(phoneValue)) {
                newErrors.phone = t`Phone number must contain at least one digit`
            } else if (phoneValue.length > 20) {
                newErrors.phone = t`Phone number must be 20 characters or less`
            }
        }

        // Validate label format if label exists
        if (hasLabel) {
            const labelValue = field === 'label' ? value : formData.label
            if (labelValue.trim().length > 50) {
                newErrors.label = t`Label must be 50 characters or less`
            }
        }

        setErrors(newErrors)
    }

    const handleSave = async () => {
        // Validate before saving
        if (!validateForm()) {
            return
        }

        // If both fields are empty, send null to delete the entry
        const hasPhone = formData.phone.trim().length > 0
        const hasLabel = formData.label.trim().length > 0

        const saveData: AdditionalPhoneData = {
            phone: hasPhone && hasLabel ? formData.phone.trim() : null,
            label: hasPhone && hasLabel ? formData.label.trim() : null,
            slot
        }

        try {
            await onSave(saveData)
            onClose()
        } catch (error) {
            // Error handling is done in the parent component
            console.error(t`Error saving additional phone number:`, error)
        }
    }

    const handleCancel = () => {
        // Reset form to original values
        setFormData({
            label: existingData.label,
            phone: existingData.phone
        })
        setErrors({})
        onClose()
    }

    // Validate on blur or when trying to save
    const handleBlur = (field: keyof FormData) => {
        // Trigger validation for the specific field
        validateForm()
    }

    // Check if form is valid - both fields must be filled OR both empty (to delete)
    const isValid = (): boolean => {
        const hasLabel = formData.label.trim().length > 0
        const hasPhone = formData.phone.trim().length > 0

        // If both are empty, that's valid (deleting the entry)
        if (!hasLabel && !hasPhone) {
            return true
        }

        // If only one is filled, it's invalid
        if ((hasLabel && !hasPhone) || (!hasLabel && hasPhone)) {
            return false
        }

        // Both are filled - check validation rules
        if (formData.label.trim().length > 50) {
            return false
        }

        if (!/^[0-9\s\-+()]+$/.test(formData.phone)) {
            return false
        }

        if (!/[0-9]/.test(formData.phone)) {
            return false
        }

        if (formData.phone.length > 20) {
            return false
        }

        return true
    }

    const isFormValid = isValid()

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth='sm' fullWidth>
            <DialogTitle>
                <Box display='flex' alignItems='center' justifyContent='space-between'>
                    <Typography variant='h6' component='div'>
                        {editingSlot ? t`Edit Additional Phone Number` : t`Add Additional Phone Number`}
                    </Typography>
                    <IconButton aria-label='close' onClick={handleCancel} size='small'>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {loading && <LinearProgress />}

            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    handleSave()
                }}
            >
                <DialogContent dividers sx={{ pt: 2, pb: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {!hasChanges && (
                            <Alert severity='info' sx={{ mb: 2 }}>
                                {t`No changes detected. Make changes to enable the Save button.`}
                            </Alert>
                        )}

                        <TextField
                            label={t`Label`}
                            placeholder={t`e.g., Work, Home, Emergency`}
                            fullWidth
                            value={formData.label}
                            onChange={(e) => handleInputChange('label', e.target.value)}
                            onBlur={() => handleBlur('label')}
                            error={!!errors.label}
                            helperText={errors.label || t`Required when phone is provided. Max 50 chars.`}
                            disabled={loading}
                            inputProps={{ maxLength: 50 }}
                            FormHelperTextProps={{ sx: { marginTop: 1, marginBottom: 0 } }}
                        />

                        <Box sx={{ mb: 4 }}>
                            <TextField
                                label={t`Phone Number`}
                                placeholder={t`e.g., +1 638-383-9939 or (123) 456-7890`}
                                fullWidth
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                onBlur={() => handleBlur('phone')}
                                error={!!errors.phone}
                                helperText={errors.phone || t`Required when label is provided. Max 20 chars.`}
                                disabled={loading}
                                inputProps={{ maxLength: 20 }}
                                FormHelperTextProps={{ sx: { marginTop: 1, marginBottom: 0 } }}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCancel} disabled={loading}>
                        {t`Cancel`}
                    </Button>
                    <Button type='submit' variant='contained' disabled={!isFormValid || !hasChanges || loading} color='primary'>
                        {loading ? t`Saving...` : t`Save`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
